import { useMemo, useState } from 'react';
import { formatDate } from '../../lib/format';
import type {
  AdminBusinessRead,
  ArmMetrics,
  LearningMemoryHealth,
  LearningMetricsRead,
} from '../../types/api';
import { fetchAdminBusinesses, type LearningMetricsQuery } from './adminApi';
import {
  armLabel,
  defaultLearningRange,
  formatRate,
  formatShare,
  holdoutLabel,
  learningRangeError,
  sortArms,
  type LearningRange,
} from './learningMetrics';
import { useLearningMetrics } from './useLearningMetrics';
import { usePagedList } from './usePagedList';

function businessId(business: AdminBusinessRead): string {
  return business.id;
}

const ARM_COLUMNS = (
  <>
    <th scope="col">Requests</th>
    <th scope="col">First draft accepted</th>
    <th scope="col">Revisions / request</th>
    <th scope="col">Regenerations / request</th>
    <th scope="col">Edited</th>
    <th scope="col">Mean edit size</th>
  </>
);

function ArmCells({ arm }: { arm: ArmMetrics }) {
  return (
    <>
      <td className="admin-table__number">{arm.requests}</td>
      <td className="admin-table__number">{formatShare(arm.first_draft_accepted)}</td>
      <td className="admin-table__number">{formatRate(arm.revisions_per_request)}</td>
      <td className="admin-table__number">{formatRate(arm.regenerations_per_request)}</td>
      <td className="admin-table__number">{formatShare(arm.edited_share)}</td>
      <td className="admin-table__number">{formatShare(arm.mean_edit_ratio)}</td>
    </>
  );
}

function ArmName({ arm }: { arm: ArmMetrics }) {
  return (
    <>
      {armLabel(arm.arm)}
      {arm.comparable ? null : <span className="learning-few">too few to compare</span>}
    </>
  );
}

const HEALTH_FIELDS: readonly { key: keyof LearningMemoryHealth; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'candidate', label: 'Candidates' },
  { key: 'retired', label: 'Retired' },
  { key: 'stated', label: 'Stated' },
  { key: 'learned', label: 'Learned' },
  { key: 'suggestions_pending', label: 'Suggestions pending' },
  { key: 'suggestions_accepted', label: 'Accepted' },
  { key: 'suggestions_dismissed', label: 'Dismissed' },
  { key: 'distill_runs_failed', label: 'Failed runs' },
  { key: 'brands_distilled', label: 'Brands learned' },
];

function LearningReport({ metrics }: { metrics: LearningMetricsRead }) {
  const arms = sortArms(metrics.arms);
  const brands = metrics.businesses.filter((brand) => brand.arms.length > 0);

  return (
    <>
      <dl className="learning-facts">
        <div>
          <dt>Window</dt>
          <dd>
            {formatDate(metrics.since)} to {formatDate(metrics.until)}
          </dd>
        </div>
        <div>
          <dt>Holdout</dt>
          <dd>
            {holdoutLabel(metrics.holdout_pct)}
            <span className="learning-facts__hint">Set with BRAND_MEMORY_HOLDOUT_PCT</span>
          </dd>
        </div>
        <div>
          <dt>Compare from</dt>
          <dd>{metrics.min_arm_size} requests per arm</dd>
        </div>
      </dl>

      <section className="learning-block">
        <h3 className="admin-panel__subtitle">By arm</h3>
        {arms.length === 0 ? (
          <p className="admin-empty">No finished requests in this window.</p>
        ) : (
          <div className="learning-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">Arm</th>
                  {ARM_COLUMNS}
                </tr>
              </thead>
              <tbody>
                {arms.map((arm) => (
                  <tr key={arm.arm}>
                    <th scope="row">
                      <ArmName arm={arm} />
                    </th>
                    <ArmCells arm={arm} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="learning-block">
        <h3 className="admin-panel__subtitle">By brand</h3>
        {brands.length === 0 ? (
          <p className="admin-empty">No brand had finished requests in this window.</p>
        ) : (
          <div className="learning-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">Brand</th>
                  <th scope="col">Arm</th>
                  {ARM_COLUMNS}
                </tr>
              </thead>
              <tbody>
                {brands.flatMap((brand) =>
                  sortArms(brand.arms).map((arm, index, list) => (
                    <tr key={`${brand.business_id}:${arm.arm}`}>
                      {index === 0 ? (
                        <th scope="rowgroup" rowSpan={list.length}>
                          {brand.name}
                        </th>
                      ) : null}
                      <td>
                        <ArmName arm={arm} />
                      </td>
                      <ArmCells arm={arm} />
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="learning-block">
        <h3 className="admin-panel__subtitle">Memory health</h3>
        <div className="learning-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                {HEALTH_FIELDS.map((field) => (
                  <th key={field.key} scope="col">
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {HEALTH_FIELDS.map((field) => (
                  <td key={field.key} className="admin-table__number">
                    {metrics.memory[field.key]}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/**
 * Does brand memory help? First-draft acceptance, revisions, regenerations and
 * caption edits per learning arm, for a window and optionally one brand, plus
 * a count of the memories themselves. Every number carries its `n`, and an arm
 * under the minimum says it is too few to compare.
 */
export function AdminLearning() {
  const [range, setRange] = useState<LearningRange>(() => defaultLearningRange());
  const [brand, setBrand] = useState('');
  const businesses = usePagedList(fetchAdminBusinesses, businessId);

  const rangeError = learningRangeError(range);
  const query = useMemo<LearningMetricsQuery | null>(
    () =>
      rangeError === null
        ? { since: range.since, until: range.until, businessId: brand || null }
        : null,
    [rangeError, range.since, range.until, brand],
  );
  const result = useLearningMetrics(query);

  return (
    <div className="admin-pane">
      <div className="learning-controls">
        <label className="field">
          <span className="field__label">From</span>
          <input
            className="field__input"
            type="date"
            value={range.since}
            max={range.until || undefined}
            onChange={(event) => setRange((current) => ({ ...current, since: event.target.value }))}
          />
        </label>
        <label className="field">
          <span className="field__label">To</span>
          <input
            className="field__input"
            type="date"
            value={range.until}
            min={range.since || undefined}
            onChange={(event) => setRange((current) => ({ ...current, until: event.target.value }))}
          />
        </label>
        <label className="field">
          <span className="field__label">Brand</span>
          <select
            className="field__input"
            value={brand}
            onChange={(event) => setBrand(event.target.value)}
          >
            <option value="">All brands</option>
            {businesses.items.map((business) => (
              <option key={business.id} value={business.id}>
                {business.name}
              </option>
            ))}
          </select>
        </label>
        {businesses.hasMore ? (
          <button
            className="button button--ghost"
            type="button"
            onClick={businesses.loadMore}
            disabled={businesses.isLoadingMore}
          >
            {businesses.isLoadingMore ? 'Loading' : 'More brands'}
          </button>
        ) : null}
        <button
          className="button button--ghost"
          type="button"
          onClick={() => setRange(defaultLearningRange())}
        >
          Last 30 days
        </button>
      </div>

      {businesses.error ? <p className="admin-empty">Brand list: {businesses.error}</p> : null}

      {rangeError ? (
        <p className="form__error" role="alert">
          {rangeError}
        </p>
      ) : result.isLoading ? (
        <p className="kb-section__status" role="status">
          Loading learning numbers
        </p>
      ) : result.error ? (
        <div className="admin-pane__foot">
          <p className="form__error" role="alert">
            {result.error}
          </p>
          <button className="button" type="button" onClick={result.retry}>
            Try again
          </button>
        </div>
      ) : result.metrics ? (
        <LearningReport metrics={result.metrics} />
      ) : null}
    </div>
  );
}
